import React, { useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import { products } from "../../data/products";

export default function FeaturedProducts() {
  const scrollerRef = useRef(null);

  const featured = useMemo(() => {
    const picked = products.filter((p) => p.featured);
    return picked.length ? picked : products;
  }, []);

  const scrollByAmount = (dir = 1) => {
    const el = scrollerRef.current;
    if (!el) return;
    const amount = Math.round(el.clientWidth * 0.9) * dir;
    el.scrollBy({ left: amount, behavior: "smooth" });
  };

  const onWheel = (e) => {
    const el = scrollerRef.current;
    if (!el) return;

    // Convert vertical wheel to horizontal scroll when hovering carousel
    if (Math.abs(e.deltaY) > Math.abs(e.deltaX) && !e.shiftKey) {
      e.preventDefault();
      el.scrollLeft += e.deltaY;
    }
  };

  return (
    <section className="bg-white py-16 sm:py-20">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h2 className="mt-0 text-3xl font-semibold tracking-tight text-slate-900 sm:text-4xl">
              Featured Products
            </h2>
          </div>

          <Link
            to="/shop"
            className="inline-flex items-center justify-center rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-900 transition hover:bg-slate-50 active:scale-[0.99]"
          >
            View all <span className="ml-1">→</span>
          </Link>
        </div>

        {/* Scroll Gallery (with side arrows) */}
        <div className="mt-6 mb-6 relative">
          {/* Left arrow (floats on left edge of first visible card) */}
          <button
  type="button"
  onClick={() => scrollByAmount(-1)}
  aria-label="Scroll left"
  className="absolute left-[-18px] top-1/2 z-20 -translate-y-1/2
             text-black/60 transition hover:text-black active:scale-[0.95]"
>
  <span className="text-6xl leading-none select-none">‹</span>
</button>

<button
  type="button"
  onClick={() => scrollByAmount(1)}
  aria-label="Scroll right"
  className="absolute right-[-18px] top-1/2 z-20 -translate-y-1/2
             text-black/60 transition hover:text-black active:scale-[0.95]"
>
  <span className="text-6xl leading-none select-none">›</span>
</button>


          {/* Fade edges so arrows feel integrated */}
          <div className="pointer-events-none absolute left-0 top-0 z-10 h-full w-12 bg-gradient-to-r from-white to-transparent" />
          <div className="pointer-events-none absolute right-0 top-0 z-10 h-full w-12 bg-gradient-to-l from-white to-transparent" />

          <div
            ref={scrollerRef}
            onWheel={onWheel}
            className="hide-scrollbar flex gap-6 overflow-x-auto pb-2 scroll-smooth snap-x snap-mandatory"
            style={{ overscrollBehaviorX: "contain" }}
          >
            {/* hide scrollbar (webkit) */}
            <style>{`
              .hide-scrollbar::-webkit-scrollbar { display: none; }
            `}</style>

            {featured.map((p) => (
              <div
                key={p.id}
                className="snap-start shrink-0 w-[78%] sm:w-[44%] lg:w-[24%]"
              >
                <TiltCard to={`/product/${p.id}`}>
                  <div className="relative aspect-square overflow-hidden rounded-2xl bg-gradient-to-b from-slate-50 to-white">
                    {/* soft glow */}
                    <div className="pointer-events-none absolute -left-10 -top-10 h-40 w-40 rounded-full bg-slate-200/60 blur-2xl opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
                    <div className="pointer-events-none absolute -bottom-10 -right-10 h-40 w-40 rounded-full bg-slate-200/60 blur-2xl opacity-0 transition-opacity duration-300 group-hover:opacity-100" />

                    <img
                      src={p.images?.[0]}
                      alt={p.name || "Product image"}
                      loading="lazy"
                      className="h-full w-full object-cover p-1 transition-transform duration-300 will-change-transform group-hover:scale-[1.18]"
                    />

                    {p.category && (
                      <div className="absolute left-4 top-4">
                        <span className="rounded-full bg-white/90 px-2.5 py-1 text-[11px] font-semibold text-slate-800 shadow-sm ring-1 ring-slate-200 backdrop-blur">
                          {p.category}
                        </span>
                      </div>
                    )}

                    {/* hover overlay */}
                    <div className="pointer-events-none absolute inset-0 bg-black/0 transition duration-300 group-hover:bg-black/10" />

                    {/* hover text */}
                    <div className="pointer-events-none absolute bottom-4 left-4 flex items-center gap-2 rounded-xl bg-white/90 px-3 py-2 text-xs font-semibold text-slate-900 opacity-0 shadow-sm ring-1 ring-slate-200 backdrop-blur transition duration-300 group-hover:opacity-100">
                      View product{" "}
                      <span className="transition-transform duration-300 group-hover:translate-x-0.5">
                        →
                      </span>
                    </div>
                  </div>
                </TiltCard>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/**
 * Smooth 3D tilt:
 * - rAF throttling (smooth)
 * - transitions OFF while moving, ON when leaving
 * - pointer events (great with trackpads)
 */
function TiltCard({ to, children }) {
  const rafRef = useRef(null);
  const lastRef = useRef({ x: 0, y: 0, rect: null });

  const apply = (card) => {
    const inner = card.querySelector("[data-tilt-inner]");
    if (!inner) return;

    const { x, y, rect } = lastRef.current;
    if (!rect) return;

    const px = (x / rect.width - 0.5) * 2;
    const py = (y / rect.height - 0.5) * 2;

    const ROT = 7;
    const rx = (-py * ROT).toFixed(2);
    const ry = (px * ROT).toFixed(2);

    inner.style.transform = `rotateX(${rx}deg) rotateY(${ry}deg) translateZ(0)`;
    rafRef.current = null;
  };

  const onEnter = (e) => {
    const card = e.currentTarget;
    const inner = card.querySelector("[data-tilt-inner]");
    if (!inner) return;
    inner.style.transition = "none";
  };

  const onMove = (e) => {
    const card = e.currentTarget;
    const rect = card.getBoundingClientRect();

    lastRef.current = {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
      rect,
    };

    if (rafRef.current) return;
    rafRef.current = requestAnimationFrame(() => apply(card));
  };

  const onLeave = (e) => {
    const card = e.currentTarget;
    const inner = card.querySelector("[data-tilt-inner]");
    if (!inner) return;

    inner.style.transition =
      "transform 320ms cubic-bezier(.2,.8,.2,1), box-shadow 300ms, border-color 300ms";
    inner.style.transform = "rotateX(0deg) rotateY(0deg) translateZ(0)";

    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
  };

  return (
    <Link
      to={to}
      onPointerEnter={onEnter}
      onPointerMove={onMove}
      onPointerLeave={onLeave}
      className="group block [perspective:1200px]"
      aria-label="View product"
    >
      <div
        data-tilt-inner
        className="relative overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm will-change-transform
                   transition-all duration-300
                   group-hover:-translate-y-1 group-hover:shadow-lg group-hover:border-slate-300"
        style={{ transformStyle: "preserve-3d" }}
      >
        <div className="pointer-events-none absolute inset-0 rounded-2xl ring-0 ring-blue-500/0 transition duration-300 group-hover:ring-2 group-hover:ring-blue-500/10" />
        {children}
      </div>
    </Link>
  );
}
